import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Group,
  RoleKey,
  RoleNameMap,
  Severity,
  useCurrentAccount,
  useGroups,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { ReactNode } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { Text } from 'react-native-paper';
import { tokens } from '../crop-circle/tokens';
import { Colors } from '../../themes/colors';
import { CampaignsIcon } from '../crop-circle/icons';
import { preferencesStyles } from './styles';
import { notificationsSection } from './utils';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type NotificationPreferencesProps = {
  control: Control<any>;
  disabled?: boolean;
};

// Chip styled to match the activity filter page (the `optionChip` family in
// filter/filterContent.tsx) so the notifications settings and the filter read
// as the same UI — same size, radius, padding, and selected treatment.
const NotificationChip = ({
  selected,
  onPress,
  disabled,
  icon,
  testID,
  children,
}: {
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  testID?: string;
  children: ReactNode;
}) => (
  <Pressable
    onPress={disabled ? undefined : onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityState={{ selected, disabled: !!disabled }}
    testID={testID}
    style={[
      styles.optionChip,
      selected && styles.optionChipSelected,
      disabled && styles.optionChipDisabled,
    ]}
  >
    {icon}
    <Text
      style={[styles.optionChipText, selected && styles.optionChipTextSelected]}
    >
      {children}
    </Text>
  </Pressable>
);

export const NotificationPreferences = ({
  control,
  disabled,
}: NotificationPreferencesProps) => {
  const { account } = useCurrentAccount();
  const { groups } = useGroups(account?.workspace?.id);
  const { labels } = useWorkspaceLabels(account?.workspace?.id);

  const canSelectLabelNotifications = Boolean(
    account?.roles?.some(
      (role) =>
        role.name === RoleNameMap[RoleKey.GENERAL] ||
        role.name === RoleNameMap[RoleKey.WORKSPACE_ADMIN] ||
        role.name === RoleNameMap[RoleKey.GLOBAL_ADMIN]
    )
  );

  return (
    <View style={preferencesStyles.contentContainer}>
      {/* GENERAL Section */}
      <View>
        <Text style={preferencesStyles.sectionTitle}>GENERAL</Text>
        <View style={styles.optionsContainer}>
          <GeneralNotificationButtons control={control} disabled={disabled} />
        </View>
      </View>

      {/* Activity Label Notifications Section */}
      {account &&
        account.workspace &&
        canSelectLabelNotifications &&
        labels &&
        labels.length > 0 && (
          <View>
            <Text style={preferencesStyles.sectionTitle}>ACTIVITY LABELS</Text>
            <View style={styles.optionsContainer}>
              <LabelNotificationButtons
                control={control}
                labels={labels}
                disabled={disabled}
              />
            </View>
          </View>
        )}

      {/* GROUPS Section */}
      {account && account.workspace && groups && groups.length > 0 && (
        <View>
          <Text style={preferencesStyles.sectionTitle}>GROUPS</Text>
          <View style={styles.optionsContainer}>
            <GroupNotificationButtons
              control={control}
              groups={groups}
              disabled={disabled}
            />
          </View>
        </View>
      )}
    </View>
  );
};

// General Notification Buttons Component
type GeneralNotificationButtonsProps = {
  control: Control<any>;
  disabled?: boolean;
};

const GeneralNotificationButtons = ({
  control,
  disabled,
}: GeneralNotificationButtonsProps) => {
  const { t } = useTranslation();
  const allSeverities = [Severity.Red, Severity.Purple, Severity.Blue];

  return (
    <Controller
      name="notificationPreferences"
      control={control}
      render={({ field: { onChange, value } }) => {
        const newIssueSeverities = value?.newIssueSeverities || [];
        const issueReplies = value?.issueReplies || false;
        // Default-on: treat null/undefined as opted-in (matches backend behaviour)
        const campaignMessages = value?.campaignMessages ?? true;

        const allSelected =
          allSeverities.every((s) => newIssueSeverities?.includes(s)) &&
          issueReplies &&
          campaignMessages;

        const handleAllToggle = () => {
          if (allSelected) {
            // Deselect all
            onChange({
              ...value,
              newIssueSeverities: [],
              issueReplies: false,
              campaignMessages: false,
            });
          } else {
            // Select all
            onChange({
              ...value,
              newIssueSeverities: allSeverities,
              issueReplies: true,
              campaignMessages: true,
            });
          }
        };

        const handleSeverityToggle = (severity: Severity) => {
          const current = newIssueSeverities;
          const newSeverities = current.includes(severity)
            ? current.filter((s: string) => s !== severity)
            : [...current, severity];
          onChange({
            ...value,
            newIssueSeverities: newSeverities,
          });
        };

        const handleRepliesToggle = () => {
          onChange({
            ...value,
            issueReplies: !issueReplies,
          });
        };

        const handleCampaignMessagesToggle = () => {
          onChange({
            ...value,
            campaignMessages: !campaignMessages,
          });
        };

        return (
          <>
            {/* All */}
            <NotificationChip
              selected={allSelected}
              onPress={handleAllToggle}
              disabled={disabled}
              testID="notifications-general-all-chip"
            >
              All
            </NotificationChip>

            {/* Individual Severity chips */}
            {notificationsSection
              .filter((n) => n.name === 'newIssueSeverities')
              .map((notification) => {
                const isSelected = !!newIssueSeverities?.includes(
                  notification.value
                );
                return (
                  <NotificationChip
                    key={notification.value}
                    selected={isSelected}
                    onPress={() =>
                      handleSeverityToggle(notification.value as Severity)
                    }
                    disabled={disabled}
                    icon={
                      <View
                        style={[
                          styles.iconContainer,
                          { backgroundColor: notification.backgroundColor },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name={notification.icon as any}
                          size={notification.sizeIcon}
                          color={notification.color}
                        />
                      </View>
                    }
                  >
                    {t(notification.labelKey)}
                  </NotificationChip>
                );
              })}

            {/* Messages/Replies chip */}
            {notificationsSection
              .filter((n) => n.name === 'issueReplies')
              .map((notification) => (
                <NotificationChip
                  key={notification.value}
                  selected={issueReplies}
                  onPress={handleRepliesToggle}
                  disabled={disabled}
                  icon={
                    <MaterialCommunityIcons
                      name={notification.icon as any}
                      size={notification.sizeIcon}
                      color={
                        issueReplies
                          ? Colors.role.textOnInverse
                          : Colors.role.textStrong
                      }
                    />
                  }
                >
                  {t(notification.labelKey)}
                </NotificationChip>
              ))}

            {/* Campaigns chip */}
            <NotificationChip
              selected={campaignMessages}
              onPress={handleCampaignMessagesToggle}
              disabled={disabled}
              testID="campaign-messages-toggle"
              icon={
                <CampaignsIcon
                  size={20}
                  color={
                    campaignMessages
                      ? Colors.role.textOnInverse
                      : Colors.role.textStrong
                  }
                />
              }
            >
              {t('preferences.notifications.campaignMessages')}
            </NotificationChip>
          </>
        );
      }}
    />
  );
};

// Label Notification Buttons Component
type LabelNotificationButtonsProps = {
  control: Control<any>;
  labels: Array<{ id: string; name: string }>;
  disabled?: boolean;
};

const LabelNotificationButtons = ({
  control,
  labels,
  disabled,
}: LabelNotificationButtonsProps) => {
  return (
    <Controller
      name="notificationPreferences.labelIds"
      control={control}
      render={({ field: { onChange, value = [] } }) => {
        const allSelected =
          labels.length > 0 && labels.every((l) => value?.includes(l.id));

        const handleAllToggle = () => {
          if (allSelected) {
            onChange([]);
          } else {
            onChange(labels.map((l) => l.id));
          }
        };

        const handleLabelToggle = (labelId: string) => {
          const newValue = value?.includes(labelId)
            ? value.filter((id: string) => id !== labelId)
            : [...(value || []), labelId];
          onChange(newValue);
        };

        return (
          <>
            {/* All */}
            <NotificationChip
              selected={allSelected}
              onPress={handleAllToggle}
              disabled={disabled}
            >
              All
            </NotificationChip>

            {/* Individual Label chips */}
            {labels.map((label) => {
              const isSelected = !!value?.includes(label.id);
              return (
                <NotificationChip
                  key={label.id}
                  selected={isSelected}
                  onPress={() => handleLabelToggle(label.id)}
                  disabled={disabled}
                >
                  {label.name}
                </NotificationChip>
              );
            })}
          </>
        );
      }}
    />
  );
};

// Group Notification Buttons Component
type GroupNotificationButtonsProps = {
  control: Control<any>;
  groups: Group[];
  disabled?: boolean;
};

const GroupNotificationButtons = ({
  control,
  groups,
  disabled,
}: GroupNotificationButtonsProps) => {
  return (
    <Controller
      name="notificationPreferences.groupIds"
      control={control}
      render={({ field: { onChange, value = [] } }) => {
        const allSelected =
          groups.length > 0 && groups.every((g) => value?.includes(g.id));

        const handleAllToggle = () => {
          if (allSelected) {
            onChange([]);
          } else {
            onChange(groups.map((g) => g.id));
          }
        };

        const handleGroupToggle = (groupId: string) => {
          const newValue = value?.includes(groupId)
            ? value.filter((id: string) => id !== groupId)
            : [...(value || []), groupId];
          onChange(newValue);
        };

        return (
          <>
            {/* All */}
            <NotificationChip
              selected={allSelected}
              onPress={handleAllToggle}
              disabled={disabled}
            >
              All
            </NotificationChip>

            {/* Individual Group chips */}
            {groups.map((group) => {
              const isSelected = !!value?.includes(group.id);
              return (
                <NotificationChip
                  key={group.id}
                  selected={isSelected}
                  onPress={() => handleGroupToggle(group.id)}
                  disabled={disabled}
                >
                  {group.name}
                </NotificationChip>
              );
            })}
          </>
        );
      }}
    />
  );
};

const styles = themedStyleSheet((role) => ({
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  // The next styles mirror filter/filterContent.tsx `sharedStyles` so the
  // notifications page matches the activity filter page exactly.
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: 10,
    borderRadius: tokens.borderRadius['3xl'],
    backgroundColor: role.surfaceHover,
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: role.surfaceInverse,
  },
  optionChipDisabled: {
    opacity: 0.6,
  },
  optionChipText: {
    ...tokens.typography.label.large,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: role.textStrong,
  },
  optionChipTextSelected: {
    color: role.textOnInverse,
  },
  iconContainer: {
    width: 20,
    height: 20,
    borderRadius: tokens.borderRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
